'use client'

import { motion } from 'framer-motion'
import { useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import type { ComponentType } from 'react'

import {
  ArbitrageIcon,
  LayersIcon,
  RadarIcon,
  SparkleIcon,
  TrendIcon,
} from '@/components/shell-icons'
import { useHasUnreadResearch } from '@/features/assistant/components/assistant-recent-list'
import {
  HOME_LIST,
  HOME_ROW,
  HOME_SPRING,
  HomePanel,
  LiveDot,
} from '@/features/home/components/home-motion'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { SCREENER_EXCHANGES } from '@/features/screener/lib/screener-frame'
import type { TutorialId } from '@/features/tutorial/lib/tutorials'
import { cn } from '@/lib/utils'
import { tutorialRequestAtom } from '@/state/tutorial'

type ToolId = 'assistant' | 'hedge' | 'arbitrage' | 'riskSlides' | 'screener'

interface Tool {
  id: ToolId
  icon: ComponentType<{ className?: string }>
  tutorial: TutorialId
  open: () => void
  /** The live line under the description; `live` adds the beating dot. */
  stat: { text: string; live: boolean }
}

/** The Screener's sweep: a wedge that circles the icon while the card is hovered. */
function RadarSweep() {
  return (
    <span
      aria-hidden="true"
      className="from-shell-accent/40 absolute inset-0 rounded-full bg-conic to-transparent to-25% opacity-0 transition-opacity duration-300 group-hover:animate-spin group-hover:opacity-100 motion-reduce:hidden"
      style={{ animationDuration: '1.6s' }}
    />
  )
}

interface HomeToolsProps {
  snapshot: HomeSnapshot
  onOpenAssistant: () => void
  onOpenHedge: () => void
  onOpenArbitrage: () => void
  onOpenRiskSlides: () => void
  onOpenScreener: () => void
  className?: string
}

/**
 * The professional tools that used to hide in the side rail, laid out where people discover
 * them: what each one does in a sentence, what it is doing for you right now, and a "How it
 * works" that starts its tutorial on the spot.
 */
export function HomeTools({
  snapshot,
  onOpenAssistant,
  onOpenHedge,
  onOpenArbitrage,
  onOpenRiskSlides,
  onOpenScreener,
  className,
}: HomeToolsProps) {
  const t = useTranslations('trade.home.tools')
  const setTutorialRequest = useSetAtom(tutorialRequestAtom)
  const hasUnreadResearch = useHasUnreadResearch()
  const { automations, researchRunning, risk } = snapshot

  const tools: Tool[] = [
    {
      id: 'assistant',
      icon: SparkleIcon,
      tutorial: 'assistant',
      open: onOpenAssistant,
      stat: hasUnreadResearch
        ? { text: t('stats.researchReady'), live: true }
        : researchRunning > 0
          ? { text: t('stats.researchRunning', { count: researchRunning }), live: true }
          : { text: t('stats.askAnything'), live: false },
    },
    {
      id: 'hedge',
      icon: TrendIcon,
      tutorial: 'hedge',
      open: onOpenHedge,
      stat:
        automations.hedges > 0
          ? { text: t('stats.hedgesRunning', { count: automations.hedges }), live: true }
          : { text: t('stats.noHedges'), live: false },
    },
    {
      id: 'arbitrage',
      icon: ArbitrageIcon,
      tutorial: 'arb',
      open: onOpenArbitrage,
      stat:
        automations.arbs > 0
          ? { text: t('stats.arbsRunning', { count: automations.arbs }), live: true }
          : { text: t('stats.noArbs'), live: false },
    },
    {
      id: 'riskSlides',
      icon: LayersIcon,
      tutorial: 'risk-slides-page',
      open: onOpenRiskSlides,
      stat:
        risk.items.length > 0
          ? { text: t('stats.positionsToTest', { count: risk.items.length }), live: false }
          : { text: t('stats.noPositions'), live: false },
    },
    {
      id: 'screener',
      icon: RadarIcon,
      tutorial: 'screener',
      open: onOpenScreener,
      stat: { text: t('stats.venuesLive', { count: SCREENER_EXCHANGES.length }), live: true },
    },
  ]

  return (
    <HomePanel title={t('title')} className={className}>
      <p className="text-foreground/50 -mt-1 mb-3 text-xs">{t('subtitle')}</p>
      <motion.ul
        variants={HOME_LIST}
        initial="hidden"
        animate="show"
        className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-2"
      >
        {tools.map((tool) => {
          const Icon = tool.icon
          return (
            <motion.li key={tool.id} variants={HOME_ROW} className="flex">
              <motion.div
                whileHover={{ y: -3 }}
                transition={HOME_SPRING}
                className="group bg-foreground/[0.03] hover:bg-foreground/[0.06] border-foreground/10 hover:border-foreground/20 relative flex w-full flex-col gap-3 overflow-hidden rounded-md border p-4 transition-colors"
              >
                {/* The whole card opens the tool; "How it works" sits above it. */}
                <button
                  type="button"
                  onClick={tool.open}
                  aria-label={t(`items.${tool.id}.open`)}
                  className="absolute inset-0 cursor-pointer"
                />
                <span className="bg-foreground/5 text-shell-accent relative flex size-10 items-center justify-center overflow-hidden rounded-full transition-transform duration-300 ease-(--ease-spring) group-hover:scale-110">
                  {tool.id === 'screener' && <RadarSweep />}
                  <Icon
                    className={cn(
                      'relative size-5 transition-transform duration-500 ease-(--ease-spring)',
                      tool.id === 'assistant' && 'group-hover:rotate-90',
                      tool.id === 'arbitrage' && 'group-hover:-scale-x-100',
                      tool.id === 'hedge' && 'group-hover:-translate-y-0.5',
                      tool.id === 'riskSlides' && 'group-hover:translate-y-0.5'
                    )}
                  />
                </span>
                <span className="pointer-events-none relative flex flex-col gap-1">
                  <span className="text-foreground text-sm font-medium tracking-tight">
                    {t(`items.${tool.id}.title`)}
                  </span>
                  <span className="text-foreground/55 text-xs leading-5">
                    {t(`items.${tool.id}.description`)}
                  </span>
                </span>
                <span className="pointer-events-none relative mt-auto flex items-center justify-between gap-2 pt-1">
                  <span
                    className={cn(
                      'flex min-w-0 items-center gap-1.5 truncate text-xs',
                      tool.stat.live ? 'text-chart-mint' : 'text-foreground/40'
                    )}
                  >
                    {tool.stat.live && <LiveDot />}
                    {tool.stat.text}
                  </span>
                  <button
                    type="button"
                    onClick={() => setTutorialRequest({ id: tool.tutorial, source: 'hub' })}
                    className="text-foreground/50 hover:text-foreground pointer-events-auto relative cursor-pointer text-xs whitespace-nowrap underline-offset-2 transition-colors hover:underline"
                  >
                    {t('howItWorks')}
                  </button>
                </span>
              </motion.div>
            </motion.li>
          )
        })}
      </motion.ul>
    </HomePanel>
  )
}
